import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { apiFetch } from '../../api/client';
import { withUnidade } from '../../utils/storageByUnit';
import { useAuth } from '../../contexts/AuthContext';
import { useModuloFiscalData } from './useModuloFiscalData';
import EditorTRModal from '../../components/contratos/EditorTRModal';
import TrilhaStepperHeader from './TrilhaStepperHeader';
import './moduloFiscal.css';

/** step -1 = escolha de origem; 0 CI; 1 TR; 2 Encaminhar */
const STEPS_SISTEMA = [
  { id: 1, nome: 'CI — Justificativa', ordem: 1 },
  { id: 2, nome: 'TR — Termo de Referência', ordem: 2 },
  { id: 3, nome: 'Encaminhar', ordem: 3 },
];

const STEPS_PDF = [
  { id: 1, nome: 'Upload CI/TR (PDF)', ordem: 1 },
  { id: 2, nome: 'Encaminhar', ordem: 2 },
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

function corpoTemConteudo(html) {
  const t = String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .trim();
  return t.length > 0;
}

/**
 * Wizard Contratação — Fase 2:
 * SISTEMA (CI/TR editores + anexos mesclados no TR) ou PDF pronto.
 * Dados só em memória; arquivos vão direto à API.
 */
export default function ModuloFiscalContratacaoWizardPage() {
  const { fiscalId } = useParams();
  const navigate = useNavigate();
  const { unidadeSelecionada, user } = useAuth();
  const { loading: loadingBase, error: errorBase, fiscal } = useModuloFiscalData(fiscalId);

  const [origemModo, setOrigemModo] = useState(null); // null | 'SISTEMA' | 'PDF'
  const [step, setStep] = useState(0);
  const [titulo, setTitulo] = useState('');
  const [corpoHtml, setCorpoHtml] = useState('');
  const [draftId, setDraftId] = useState(null);
  const [draftCodigo, setDraftCodigo] = useState('');
  const [trOk, setTrOk] = useState(false);
  const [trOpen, setTrOpen] = useState(false);
  const [trAnexos, setTrAnexos] = useState([]); // File[] — só até upload
  const [ciPdf, setCiPdf] = useState(null);
  const [trPdf, setTrPdf] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [autorizadorIds, setAutorizadorIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [fluxoNome, setFluxoNome] = useState('');

  const basePath = `/modulos/fiscal/${fiscalId}/processo-administrativo`;

  const stepsUi = useMemo(() => {
    if (!origemModo) return [];
    return origemModo === 'PDF' ? STEPS_PDF : STEPS_SISTEMA;
  }, [origemModo]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch(withUnidade('/api/contratos/fluxos', unidadeSelecionada));
        const data = await res.json().catch(() => []);
        const fluxos = Array.isArray(data) ? data : data?.fluxos || [];
        const ativo = fluxos.find((f) => f.ativo === 1 || f.ativo === true) || fluxos[0];
        setFluxoNome(ativo?.nome || '');
      } catch {
        /* ignore */
      }
    })();
  }, [unidadeSelecionada]);

  const carregarUsuarios = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      const codigo = unidadeSelecionada?.codigo;
      if (codigo) params.append('unidade', codigo);
      const res = await apiFetch(`/api/usuarios${params.toString() ? `?${params}` : ''}`);
      const data = await res.json().catch(() => []);
      setUsuarios(Array.isArray(data) ? data : []);
    } catch {
      setUsuarios([]);
    }
  }, [unidadeSelecionada?.codigo]);

  useEffect(() => {
    carregarUsuarios();
  }, [carregarUsuarios]);

  const verificarTr = async (sid) => {
    try {
      const res = await apiFetch(`/api/contratos/solicitacoes/${sid}/tr`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return false;
      if (data?.trPreenchido || data?.temAnexoTrPdf || data?.trPdfPath) return true;
      const json = data?.trJson || data?.tr_json || data;
      const clausulas = json?.clausulas || data?.clausulas || [];
      const objeto = String(data?.campos?.objeto || json?.objeto || '').trim();
      return (
        objeto.length > 0 ||
        (Array.isArray(clausulas) && clausulas.length > 0 && data?.clausulasPreenchidas)
      );
    } catch {
      return false;
    }
  };

  const criarRascunhoSistema = async () => {
    if (draftId) return draftId;
    const formData = new FormData();
    formData.append('tipo', 'CI');
    formData.append('ciTipo', 'CONTRATACAO');
    formData.append('titulo', titulo.trim());
    formData.append('descricao', `Solicitação fiscal — setor ${fiscal?.setor || ''}`);
    formData.append('ciOrigem', 'SISTEMA');
    formData.append('trOrigem', 'SISTEMA');
    formData.append(
      'ciJson',
      JSON.stringify({ assunto: titulo.trim(), corpoHtml: corpoHtml || '' })
    );
    if (unidadeSelecionada?.codigo) formData.append('unidade', unidadeSelecionada.codigo);
    if (user?.id) formData.append('solicitanteId', String(user.id));

    const res = await apiFetch('/api/contratos/solicitacoes', { method: 'POST', body: formData });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || 'Erro ao criar CI');
    const id = data?.id || data?.solicitacao?.id;
    if (!id) throw new Error('CI criada sem ID');
    setDraftId(id);
    setDraftCodigo(data?.codigo || data?.solicitacao?.codigo || '');
    return id;
  };

  const criarRascunhoPdf = async () => {
    if (!titulo.trim()) throw new Error('Informe o assunto/título');
    if (!ciPdf && !trPdf) throw new Error('Anexe ao menos o PDF da CI ou do TR');
    if (!trPdf) throw new Error('Anexe o PDF do Termo de Referência (TR)');

    const formData = new FormData();
    formData.append('tipo', 'CI');
    formData.append('ciTipo', 'CONTRATACAO');
    formData.append('titulo', titulo.trim());
    formData.append(
      'descricao',
      `Solicitação fiscal — setor ${fiscal?.setor || ''} (CI/TR via PDF)`
    );
    formData.append('ciOrigem', 'PDF');
    formData.append('trOrigem', 'PDF');
    formData.append(
      'ciJson',
      JSON.stringify({
        assunto: titulo.trim(),
        corpoHtml: '<p>CI enviada como documento PDF pronto.</p>',
      })
    );
    if (unidadeSelecionada?.codigo) formData.append('unidade', unidadeSelecionada.codigo);
    if (user?.id) formData.append('solicitanteId', String(user.id));
    if (ciPdf) formData.append('ciFile', ciPdf);
    if (trPdf) formData.append('trFile', trPdf);

    const res = await apiFetch('/api/contratos/solicitacoes', { method: 'POST', body: formData });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || 'Erro ao criar solicitação com PDF');
    const id = data?.id || data?.solicitacao?.id;
    if (!id) throw new Error('Solicitação criada sem ID');
    setDraftId(id);
    setDraftCodigo(data?.codigo || data?.solicitacao?.codigo || '');
    setTrOk(true);
    // libera refs de arquivo da memória após upload
    setCiPdf(null);
    setTrPdf(null);
    return id;
  };

  const uploadAnexosTr = async (sid) => {
    if (!trAnexos.length) return;
    const fd = new FormData();
    trAnexos.forEach((f) => fd.append('anexos', f));
    const res = await apiFetch(`/api/contratos/solicitacoes/${sid}/tr/anexos`, {
      method: 'POST',
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao anexar PDFs ao TR');
    setTrAnexos([]);
  };

  const avancar = async () => {
    setMsg('');
    try {
      if (origemModo === 'PDF') {
        if (step === 0) {
          setSaving(true);
          await criarRascunhoPdf();
          setSaving(false);
          setStep(1); // encaminhar
          return;
        }
      } else {
        if (step === 0) {
          if (!titulo.trim()) throw new Error('Informe o assunto da CI');
          if (!corpoTemConteudo(corpoHtml)) throw new Error('Preencha a justificativa da CI');
          setSaving(true);
          await criarRascunhoSistema();
          setSaving(false);
          setStep(1);
          return;
        }
        if (step === 1) {
          if (!draftId) throw new Error('Salve a CI primeiro');
          const ok = await verificarTr(draftId);
          if (!ok) {
            setTrOk(false);
            throw new Error('Abra o editor do TR, preencha e salve antes de continuar');
          }
          setSaving(true);
          await uploadAnexosTr(draftId);
          setSaving(false);
          setTrOk(true);
          setStep(2);
          return;
        }
      }
    } catch (e) {
      setSaving(false);
      setMsg(e.message || 'Erro');
    }
  };

  const enviar = async () => {
    setMsg('');
    if (!draftId) {
      setMsg('Rascunho da CI não encontrado');
      return;
    }
    if (!autorizadorIds.length) {
      setMsg('Selecione ao menos um autorizador do TR');
      return;
    }
    const okTr = await verificarTr(draftId);
    if (!okTr) {
      setMsg('TR incompleto — anexe o PDF ou salve o TR no editor');
      if (origemModo === 'SISTEMA') setStep(1);
      else setStep(0);
      return;
    }

    setSaving(true);
    try {
      if (origemModo === 'SISTEMA' && trAnexos.length) {
        await uploadAnexosTr(draftId);
      }

      const autorizadores = autorizadorIds
        .map((id) => {
          const u = usuarios.find((x) => String(x.id) === String(id));
          if (!u?.email) return null;
          return { id: u.id, nome: u.nome, email: u.email };
        })
        .filter(Boolean);
      if (!autorizadores.length) throw new Error('Autorizadores sem e-mail cadastrado');

      if (origemModo === 'SISTEMA') {
        await apiFetch(`/api/contratos/solicitacoes/${draftId}/ci`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assunto: titulo.trim(), corpoHtml: corpoHtml || '' }),
        });
      }
      await apiFetch(`/api/contratos/solicitacoes/${draftId}/autorizadores`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autorizadores }),
      });
      const res = await apiFetch(
        `/api/contratos/solicitacoes/${draftId}/enviar-processo-contratacao`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ autorizadores }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao encaminhar');

      const processoId = data?.processoId || data?.processo?.id;
      setMsg('Processo aberto. Segue o fluxo configurado em Contratos.');
      if (processoId) navigate(`${basePath}/${processoId}`);
      else navigate(basePath);
    } catch (e) {
      setMsg(e.message || 'Erro ao enviar');
    } finally {
      setSaving(false);
    }
  };

  const fecharTr = async () => {
    setTrOpen(false);
    if (draftId) {
      const ok = await verificarTr(draftId);
      setTrOk(ok);
    }
  };

  const escolherOrigem = (modo) => {
    setOrigemModo(modo);
    setStep(0);
    setMsg('');
    setDraftId(null);
    setDraftCodigo('');
    setTrOk(false);
    setCiPdf(null);
    setTrPdf(null);
    setTrAnexos([]);
  };

  if (loadingBase) {
    return (
      <div className="mf-page" style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <CircularProgress sx={{ color: '#5ce1c5' }} />
      </div>
    );
  }

  if (errorBase || !fiscal) {
    return (
      <div className="mf-page">
        <p className="mf-page__meta">{errorBase || 'Fiscal não encontrado.'}</p>
      </div>
    );
  }

  const stepEncaminhar = origemModo === 'PDF' ? 1 : 2;

  return (
    <div className="mf-page">
      <div className="mf-page__header">
        <div>
          <h1 className="mf-page__title">Nova Contratação</h1>
          <p className="mf-page__meta">
            {fiscal.setor}
            {fluxoNome ? ` · Fluxo: ${fluxoNome}` : ''}
            {draftCodigo ? ` · ${draftCodigo}` : ''}
            {origemModo ? ` · Origem: ${origemModo === 'PDF' ? 'PDF pronto' : 'Sistema'}` : ''}
          </p>
        </div>
        <button type="button" className="mf-page__back" onClick={() => navigate(basePath)}>
          Voltar
        </button>
      </div>

      {!origemModo ? (
        <div className="mf-card">
          <h2 className="mf-section-title" style={{ marginTop: 0 }}>
            Como deseja iniciar?
          </h2>
          <p className="mf-page__meta" style={{ marginBottom: '1rem' }}>
            Escolha o caminho. Arquivos são enviados ao servidor — nada fica salvo no navegador.
          </p>
          <div className="mf-pa-tipos">
            <button type="button" className="mf-pa-tipo" onClick={() => escolherOrigem('SISTEMA')}>
              <span className="mf-pa-tipo__body">
                <strong>Preencher no sistema</strong>
                <span>CI e TR no editor (cláusulas, moldura, IA) + anexos mesclados no PDF do TR</span>
              </span>
            </button>
            <button type="button" className="mf-pa-tipo" onClick={() => escolherOrigem('PDF')}>
              <span className="mf-pa-tipo__body">
                <strong>Enviar documento pronto (PDF)</strong>
                <span>Upload da CI e/ou TR já elaborados — vai direto para autorizadores</span>
              </span>
            </button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '0.75rem' }}>
            <button
              type="button"
              className="mf-btn mf-btn--ghost"
              onClick={() => {
                setOrigemModo(null);
                setStep(0);
              }}
            >
              Trocar origem
            </button>
          </div>

          <TrilhaStepperHeader
            dense
            etapas={stepsUi.map((s, i) => ({
              ...s,
              status: i < step ? 'CONCLUIDO' : i === step ? 'EM_ANDAMENTO' : 'PENDENTE',
              ehAtual: i === step,
            }))}
            etapaAtualId={stepsUi[step]?.id}
            etapaSelecionadaId={stepsUi[step]?.id}
            onSelectEtapa={(et) => {
              const idx = stepsUi.findIndex((s) => Number(s.id) === Number(et.id));
              if (idx >= 0 && idx <= step) setStep(idx);
            }}
          />

          {msg ? (
            <p className={`mf-alert ${msg.includes('aberto') ? 'mf-alert--ok' : 'mf-alert--error'}`}>
              {msg}
            </p>
          ) : null}

          {origemModo === 'PDF' && step === 0 && (
            <div className="mf-card">
              <label className="mf-label">Assunto / título</label>
              <input
                className="mf-input"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ex.: Contratação de serviço de..."
              />
              <label className="mf-label" style={{ marginTop: '1rem' }}>
                CI (PDF) — opcional se o TR já contiver a CI
              </label>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="mf-input"
                onChange={(e) => setCiPdf(e.target.files?.[0] || null)}
              />
              <label className="mf-label" style={{ marginTop: '1rem' }}>
                TR (PDF) — obrigatório
              </label>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="mf-input"
                onChange={(e) => setTrPdf(e.target.files?.[0] || null)}
              />
              <p className="mf-page__meta" style={{ marginTop: '0.75rem' }}>
                {ciPdf ? `CI: ${ciPdf.name}` : 'CI: —'} · {trPdf ? `TR: ${trPdf.name}` : 'TR: —'}
              </p>
            </div>
          )}

          {origemModo === 'SISTEMA' && step === 0 && (
            <div className="mf-card">
              <label className="mf-label">Assunto da CI</label>
              <input
                className="mf-input"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ex.: Contratação de serviço de..."
              />
              <label className="mf-label" style={{ marginTop: '1rem' }}>
                Justificativa / corpo da CI
              </label>
              <div className="mf-quill">
                <ReactQuill
                  theme="snow"
                  value={corpoHtml}
                  onChange={setCorpoHtml}
                  modules={QUILL_MODULES}
                />
              </div>
            </div>
          )}

          {origemModo === 'SISTEMA' && step === 1 && (
            <div className="mf-card">
              <p className="mf-page__meta">
                Editor em tela cheia (cláusulas, moldura, IA). Anexos opcionais são mesclados ao final
                do PDF do TR no servidor.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1rem' }}>
                <button
                  type="button"
                  className="mf-btn mf-btn--primary"
                  disabled={!draftId}
                  onClick={() => setTrOpen(true)}
                >
                  Abrir editor do TR
                </button>
                <span className={`mf-badge ${trOk ? 'mf-badge--ok' : 'mf-badge--warn'}`}>
                  {trOk ? 'TR salvo' : 'TR pendente'}
                </span>
              </div>
              <label className="mf-label" style={{ marginTop: '1.25rem' }}>
                Anexos do TR (PDF, opcional — mesclados no TR)
              </label>
              <input
                type="file"
                accept="application/pdf,.pdf"
                multiple
                className="mf-input"
                onChange={(e) => setTrAnexos(Array.from(e.target.files || []))}
              />
              {trAnexos.length > 0 && (
                <p className="mf-page__meta" style={{ marginTop: '0.5rem' }}>
                  {trAnexos.length} arquivo(s) prontos para envio ao servidor
                </p>
              )}
            </div>
          )}

          {step === stepEncaminhar && (
            <div className="mf-card">
              <p className="mf-page__meta" style={{ marginBottom: '1rem' }}>
                Selecione quem autoriza e assina o TR. Origem registrada no processo:{' '}
                <strong>{origemModo === 'PDF' ? 'CI/TR via PDF' : 'elaborado no sistema'}</strong>.
              </p>
              <FormControl
                fullWidth
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 1 }}
              >
                <InputLabel id="aut-label" sx={{ color: '#8fb3ad' }}>
                  Autorizadores
                </InputLabel>
                <Select
                  labelId="aut-label"
                  multiple
                  value={autorizadorIds}
                  onChange={(e) => setAutorizadorIds(e.target.value)}
                  input={<OutlinedInput label="Autorizadores" />}
                  renderValue={(selected) =>
                    selected
                      .map((id) => usuarios.find((u) => String(u.id) === String(id))?.nome || id)
                      .join(', ')
                  }
                  sx={{ color: '#e8f5f2' }}
                >
                  {usuarios.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      <Checkbox checked={autorizadorIds.indexOf(u.id) > -1} />
                      <ListItemText primary={u.nome} secondary={u.email} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          )}

          <div className="mf-wizard-actions">
            <button
              type="button"
              className="mf-btn mf-btn--ghost"
              disabled={step === 0 || saving}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
            >
              Voltar
            </button>
            {step < stepEncaminhar ? (
              <button
                type="button"
                className="mf-btn mf-btn--primary"
                disabled={saving}
                onClick={avancar}
              >
                {saving ? <CircularProgress size={18} color="inherit" /> : 'Continuar'}
              </button>
            ) : (
              <button
                type="button"
                className="mf-btn mf-btn--primary"
                disabled={saving}
                onClick={enviar}
              >
                {saving ? <CircularProgress size={18} color="inherit" /> : 'Encaminhar ao fluxo'}
              </button>
            )}
          </div>

          <EditorTRModal
            open={trOpen}
            onClose={fecharTr}
            solicitacaoId={draftId}
            assuntoCI={titulo}
          />
        </>
      )}
    </div>
  );
}
